import { supabase } from '@/lib/supabase';
import { NUTRIENT_CONFIG, type NutrientKey } from '@/hooks/useMicronutrients';

/**
 * Разбор рациона за неделю (29.09.2026, замысел Славы).
 *
 * Человек записывает 5 дней подряд → открывается нутритивный разбор,
 * которым хочется поделиться.
 *
 * Числа считает сервер (`get_week_ration`), нормы берутся из того же источника,
 * что и дневной экран витаминов. Модель тут не участвует вовсе: разбор
 * открывается мгновенно и не стоит ничего.
 */

export const STREAK_GOAL = 5;

export type WeekRow = {
  key: NutrientKey;
  label: string;
  emoji: string;
  perDay: number;
  unit: string;
  percent: number;
};

export type RainbowColor = {
  id: string;
  label: string;
  dot: string;
  examples: string[];
};

export type WeekRation = {
  streak: number;
  /** Полные дни серии после последнего просмотра разбора — новый круг (миграция 061) */
  cycle: number;
  /** Разбор уже открывали — значит идёт не первый круг */
  seenBefore: boolean;
  /** Сколько разборов уже просмотрено (миграция 063) */
  reviews: number;
  /** Какой круг идёт сейчас: просмотренные + 1 */
  circle: number;
  /** Нормы в день по ключам нутриентов — для сравнения недель */
  targets: Record<string, number>;
  daysLogged: number;
  daysFull: number;
  meals: number;
  calories: number;
  perDayCalories: number;
  strong: WeekRow[];
  weak: WeekRow[];
  all: WeekRow[];
  rainbow: RainbowColor[];
  rainbowMissing: RainbowColor[];
  plants: number;
  pendingItems: number;
  unlocked: boolean;
};

const UNIT: Record<string, string> = {
  fiber: 'г', vegetables_fruits: 'г', omega3: 'г', protein: 'г',
  calcium: 'мг', iron: 'мг', magnesium: 'мг', zinc: 'мг', potassium: 'мг',
  vitamin_d: 'мкг', vitamin_b12: 'мкг', vitamin_c: 'мг',
};

const ORDER: NutrientKey[] = [
  'fiber', 'vegetables_fruits', 'omega3',
  'calcium', 'iron', 'magnesium', 'zinc', 'potassium',
  'vitamin_d', 'vitamin_b12', 'vitamin_c',
];

/**
 * «Радуга недели» — метод радуги из 15-го дня курса «Путь», сделанный видимым.
 *
 * Цвет определяется СЛОВАРЁМ, а не моделью: у всех должно получаться одинаково,
 * и это не должно стоить денег. Словарь неполный по определению — незнакомый
 * продукт просто не попадает в радугу, и это лучше, чем угадывать неверно.
 */
const COLORS: { id: string; label: string; dot: string; words: string[] }[] = [
  {
    id: 'red', label: 'Красный', dot: '#d7483f',
    words: ['томат', 'помидор', 'перец красн', 'редис', 'клубник', 'малин', 'вишн', 'черешн',
      'гранат', 'арбуз', 'грейпфрут', 'клюкв', 'брусник', 'краснокочан', 'свёкл', 'свекл', 'яблоко красн'],
  },
  {
    id: 'orange', label: 'Оранжевый', dot: '#e08a3c',
    words: ['морков', 'тыкв', 'апельсин', 'мандарин', 'хурм', 'абрикос', 'кураг', 'персик',
      'нектарин', 'манго', 'облепих', 'батат', 'перец оранж', 'дын'],
  },
  {
    id: 'yellow', label: 'Жёлтый', dot: '#d9be4a',
    words: ['банан', 'кукуруз', 'перец жёлт', 'перец желт', 'лимон', 'ананас', 'груш',
      'картоф', 'кабач', 'яблоко жёлт', 'имбир', 'куркум'],
  },
  {
    id: 'green', label: 'Зелёный', dot: '#5aa86f',
    words: ['огур', 'салат', 'шпинат', 'брокколи', 'брюссельск', 'капуст', 'руккол', 'укроп',
      'петрушк', 'киви', 'авокадо', 'зелён', 'зелен', 'горош', 'стручков', 'сельдер', 'базилик',
      'кинз', 'шпинат', 'яблоко зел', 'виноград зел', 'фасоль струч', 'спарж', 'лайм', 'мят'],
  },
  {
    id: 'purple', label: 'Фиолетовый', dot: '#7a5aa8',
    words: ['баклаж', 'черник', 'голубик', 'ежевик', 'слив', 'виноград тём', 'виноград тем',
      'виноград чёрн', 'смородин', 'инжир', 'краснокочанн', 'фиолет'],
  },
  {
    id: 'white', label: 'Белый', dot: '#c9cfc9',
    words: ['чеснок', 'лук репч', 'лук белый', 'цветная капуст', 'шампиньон', 'гриб', 'редьк',
      'дайкон', 'репа', 'кабачок белый', 'кокос', 'корень сельдере'],
  },
  {
    id: 'brown', label: 'Коричневый', dot: '#9c7a53',
    words: ['гречк', 'овсян', 'овся', 'рис бур', 'рис дик', 'киноа', 'булгур', 'перловк',
      'чечевиц', 'нут', 'фасоль', 'горох', 'орех', 'миндал', 'фундук', 'кешью', 'фисташ',
      'семечк', 'семена', 'лён', 'льнян', 'кунжут', 'чиа', 'цельнозерн', 'отруб', 'полб'],
  },
];

export function buildRainbow(items: string[]) {
  const found: RainbowColor[] = [];
  const missing: RainbowColor[] = [];
  const counted = new Set<string>();

  for (const c of COLORS) {
    const examples: string[] = [];
    for (const name of items) {
      if (c.words.some((w) => name.includes(w))) {
        // Название кладём человеческое: первое слово с заглавной.
        const short = name.split(/[,(]/)[0].trim();
        if (short && !examples.includes(short) && examples.length < 3) examples.push(short);
        counted.add(name);
      }
    }
    const color: RainbowColor = { id: c.id, label: c.label, dot: c.dot, examples };
    (examples.length ? found : missing).push(color);
  }
  return { found, missing, plants: counted.size };
}

/**
 * Разбор просмотрен — круг засчитан, начинается следующий (Слава 07.10.2026: «открыл,
 * посмотрел, поделился — должен начаться новый этап серии»). Отметку ставит сервер
 * и только если серия действительно собрана: уровни нельзя накрутить запросом.
 */
export async function markWeekRationSeen(): Promise<void> {
  const d = new Date();
  const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const { data, error } = await supabase.rpc('mark_week_ration_seen', { p_today: local });
  if (error || !data?.ok) console.error('[weekRation] mark seen failed', error ?? data?.error);
}

export async function getWeekRation(): Promise<WeekRation | null> {
  try {
    const today = new Date();
    const local = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const [{ data: week, error }, { data: summary }] = await Promise.all([
      supabase.rpc('get_week_ration', { p_today: local, p_days: 7 }),
      supabase.rpc('get_daily_nutrients_summary', { p_date: local }),
    ]);
    if (error || !week) return null;

    const w = week as any;
    const targets = (summary as any)?.targets ?? {};
    const sums = w.sums ?? {};
    // Делим на ПОЛНЫЕ дни, а не на семь: дни без записей занижали бы средний день
    // и выглядели как «мало ест», хотя человек просто не записывал.
    const days = Math.max(1, Number(w.days_full) || Number(w.days_logged) || 1);

    const all: WeekRow[] = [];
    for (const key of ORDER) {
      const total = Number(sums[key] || 0);
      const target = Number(targets[key]?.amount || 0);
      if (!(total > 0) || !(target > 0)) continue;
      const cfg = NUTRIENT_CONFIG[key];
      if (!cfg) continue;
      const perDay = total / days;
      all.push({
        key,
        label: cfg.label,
        emoji: cfg.emoji,
        perDay: perDay >= 10 ? Math.round(perDay) : Math.round(perDay * 10) / 10,
        unit: UNIT[key] ?? '',
        percent: Math.round((perDay / target) * 100),
      });
    }

    const sorted = [...all].sort((a, b) => b.percent - a.percent);
    const { found, missing, plants } = buildRainbow((w.items ?? []) as string[]);

    return {
      streak: Number(w.streak) || 0,
      cycle: Number(w.cycle ?? w.streak) || 0,
      seenBefore: !!w.seen_on || Number(w.reviews) > 0,
      reviews: Number(w.reviews) || 0,
      circle: (Number(w.reviews) || 0) + 1,
      targets: Object.fromEntries(ORDER.map((k) => [k, Number(targets[k]?.amount || 0)])),
      daysLogged: Number(w.days_logged) || 0,
      daysFull: Number(w.days_full) || 0,
      meals: Number(w.meals) || 0,
      calories: Number(w.calories) || 0,
      perDayCalories: Math.round((Number(w.calories) || 0) / days),
      strong: sorted.slice(0, 3),
      // Провалы показываем только внутри приложения — наружу они не идут.
      weak: sorted.filter((r) => r.percent < 70).slice(-3).reverse(),
      all,
      rainbow: found,
      rainbowMissing: missing,
      plants,
      pendingItems: Number(w.pending_items) || 0,
      // Открыт, пока не просмотрен: после просмотра серия идёт новым кругом
      unlocked: (Number(w.cycle ?? w.streak) || 0) >= STREAK_GOAL,
    };
  } catch (e) {
    console.error('week ration error', e);
    return null;
  }
}
